import axios from "axios";
import type {
  ChatMessage,
  Client,
  ClientConversationSummary,
  ClientHistoryResponse,
  CoachConversationSummary,
  DashboardOverview,
  NotificationsResponse,
  ProgressPhotoType,
  UserProfile,
  WeeklyCheckIn,
} from "../types";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

export const apiBaseUrl = API_BASE_URL;

export const api = axios.create({
  baseURL: API_BASE_URL,
});

export function setAuthToken(token: string | null) {
  if (token) {
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common.Authorization;
  }
}

export async function login(email: string, password: string) {
  const response = await api.post("/auth/login", { email, password });
  return response.data as {
    access_token: string;
    user: UserProfile;
  };
}

export async function registerClient(payload: Record<string, unknown>) {
  const response = await api.post("/auth/register-client", payload);
  return response.data as {
    access_token: string;
    user: UserProfile;
  };
}

export async function getProfile() {
  const response = await api.get("/auth/profile");
  return response.data as UserProfile;
}

export async function getDashboardOverview() {
  const response = await api.get("/dashboard/overview");
  return response.data as DashboardOverview;
}

export async function getClients() {
  const response = await api.get("/clients");
  return response.data as Client[];
}

export async function createClient(payload: Record<string, unknown>) {
  const response = await api.post("/clients", payload);
  return response.data as Client;
}

export async function updateClient(clientId: string, payload: Record<string, unknown>) {
  const response = await api.patch(`/clients/${clientId}`, payload);
  return response.data as Client;
}

export async function deleteClient(clientId: string) {
  await api.delete(`/clients/${clientId}`);
}

export async function getClient(clientId: string) {
  const response = await api.get(`/clients/${clientId}`);
  return response.data;
}

export async function getClientHistory(clientId: string) {
  const response = await api.get(`/clients/${clientId}/history`);
  return response.data as ClientHistoryResponse;
}

export async function getMyClient() {
  const response = await api.get("/clients/me");
  return response.data;
}

export async function getMyClientHistory() {
  const response = await api.get("/clients/me/history");
  return response.data as ClientHistoryResponse;
}

export async function saveInitialAssessment(clientId: string, payload: Record<string, unknown>) {
  const response = await api.post(`/clients/${clientId}/initial-assessment`, payload);
  return response.data;
}

export async function saveMyInitialAssessment(payload: Record<string, unknown>) {
  const response = await api.post("/clients/me/initial-assessment", payload);
  return response.data;
}

export async function saveWeeklyCheckIn(clientId: string, payload: Record<string, unknown>) {
  const response = await api.post(`/clients/${clientId}/check-ins`, payload);
  return response.data;
}

export async function saveMyWeeklyCheckIn(payload: Record<string, unknown>) {
  const response = await api.post("/clients/me/check-ins", payload);
  return response.data;
}

export async function uploadMyWeeklyCheckInProgressPhoto(type: ProgressPhotoType, file: File) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("type", type);
  const response = await api.post("/clients/me/check-ins/progress-photos", formData).catch((error) => {
    if (error?.response?.status === 404) {
      return api.post("/clients/me/progress-photos", formData);
    }
    throw error;
  });
  return response.data as { type: ProgressPhotoType; imageUrl: string };
}

export async function uploadWeeklyCheckInProgressPhoto(clientId: string, type: ProgressPhotoType, file: File) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("type", type);
  const response = await api.post(`/clients/${clientId}/check-ins/progress-photos`, formData).catch((error) => {
    if (error?.response?.status === 404) {
      return api.post(`/clients/${clientId}/progress-photos`, formData);
    }
    throw error;
  });
  return response.data as { type: ProgressPhotoType; imageUrl: string };
}

export async function uploadMyProfilePhoto(file: File) {
  const formData = new FormData();
  formData.append("file", file);
  const response = await api.post("/auth/me/profile-photo", formData);
  return response.data as { profile_photo_url: string };
}

export async function getNotifications() {
  const response = await api.get("/notifications");
  return response.data as NotificationsResponse;
}

export async function markNotificationRead(id: string) {
  const response = await api.post(`/notifications/${id}/read`);
  return response.data;
}

export async function markAllNotificationsRead() {
  const response = await api.post(`/notifications/read-all`);
  return response.data;
}

export async function validateCheckIn(clientId: string, checkInId: string) {
  const response = await api.post(`/clients/${clientId}/check-ins/${checkInId}/validate`);
  return response.data as WeeklyCheckIn;
}

export async function generateRecommendation(clientId: string) {
  const response = await api.post(`/recommendations/generate/${clientId}`);
  return response.data;
}

export async function approveRecommendation(recommendationId: string, coachNotes?: string) {
  const response = await api.patch(`/recommendations/${recommendationId}/approve`, {
    coachNotes,
  });
  return response.data;
}

export async function listMessageConversations() {
  const response = await api.get("/messages/conversations");
  return response.data as CoachConversationSummary[];
}

export async function getMyMessageConversation() {
  const response = await api.get("/messages/my-coach");
  return response.data as ClientConversationSummary;
}

export async function listMessagesWith(clientId: string) {
  const response = await api.get(`/messages/with/${clientId}`);
  return response.data as ChatMessage[];
}

export async function sendMessageTo(clientId: string, content: string) {
  const response = await api.post(`/messages/with/${clientId}`, { content });
  return response.data as ChatMessage;
}

export async function markConversationRead(clientId: string) {
  await api.post(`/messages/with/${clientId}/read`);
}

export function openMessagesStream(clientId: string, token: string) {
  const url = new URL(`${API_BASE_URL}/messages/stream/${clientId}`);
  url.searchParams.set("access_token", token);
  return new EventSource(url.toString());
}
